import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Pause,
  Search,
  ChevronDown,
  Filter,
  Plus,
  Play,
  Clock,
  Settings,
  ArrowRight,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from "recharts";
import axios from "../config/axios";
import toast from "react-hot-toast";

/**
 * Mock data for the activity chart
 * In a real implementation, this would come from an aggregated analytics endpoint
 */
const MOCK_CHART_DATA = [];

/**
 * Connections Page
 * Re-implemented to match Airbyte UI with Table view and Activity Chart
 */
const Connections = () => {
  const navigate = useNavigate();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Stats
  const stats = useMemo(() => {
    return {
      running: connections.filter((c) => c.status === "active" || c.status === "running").length,
      healthy: connections.filter((c) => c.lastSyncStatus === "succeeded" || c.status === "active").length,
      paused: connections.filter((c) => c.status === "paused" || c.status === "inactive").length,
      notSynced: connections.filter((c) => !c.lastSyncAt).length,
    };
  }, [connections]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [sourcesResponse, connectorsResponse] = await Promise.all([
        axios.get(`/api/connectors/user/sources`),
        axios.get(`/api/connectors`)
      ]);

      const sources = sourcesResponse.data.sources || [];
      const allConnectors = [
        ...connectorsResponse.data.featured,
        ...connectorsResponse.data.connectors
      ];

      const connectorMap = new Map();
      allConnectors.forEach(c => connectorMap.set(c.id, c));

      const enrichedSources = sources.map(source => {
        const metadata = connectorMap.get(source.connectorId);
        return {
          ...source,
          sourceType: metadata?.name || source.connectorId,
          iconUrl: metadata?.iconUrl
        };
      });

      setConnections(enrichedSources);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load connections");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (connection, e) => {
    e.stopPropagation();
    const newStatus = connection.status === "active" ? "inactive" : "active";
    try {
      // Optimistic update
      setConnections(prev => prev.map(c =>
        c.id === connection.id ? { ...c, status: newStatus } : c
      ));

      await axios.patch(
        `/api/connectors/connections/${connection.airbyteConnectionId}/status`,
        { status: newStatus }
      );
      toast.success(newStatus === "active" ? "Connection enabled" : "Connection disabled");
    } catch (error) {
      console.error("Failed to update status:", error);
      toast.error("Failed to update status");
      fetchData(); // Revert on error
    }
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays === 0) {
      const diffHours = Math.floor(diffMs / 3600000);
      if (diffHours === 0) {
        const diffMins = Math.floor(diffMs / 60000);
        return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
      }
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    }
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  };

  // Filtering
  const filteredConnections = connections.filter(connection => {
    const matchesText = connection.connectorName.toLowerCase().includes(filterText.toLowerCase());
    const matchesStatus = statusFilter === "all" ||
      (statusFilter === "active" && connection.status === "active") ||
      (statusFilter === "paused" && (connection.status === "paused" || connection.status === "inactive")) ||
      (statusFilter === "failed" && (connection.status === "failed" || connection.lastSyncStatus === "failed"));

    return matchesText && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Connections</h1>
            <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
              <span className="text-primary-600 font-medium">{stats.running} running</span>
              <span>•</span>
              <span className="text-green-600">{stats.healthy} healthy</span>
              <span>•</span>
              <span className="text-gray-500">{stats.paused} paused</span>
              <span>•</span>
              <span className="text-orange-500">{stats.notSynced} not synced</span>
            </div>
          </div>
          <button
            onClick={() => navigate("/connectors")}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            New connection
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm flex flex-wrap gap-4 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search..."
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
            />
          </div>

          <select
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All connections</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="failed">Failed</option>
          </select>

          <select className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
            <option>All statuses</option>
            <option>Healthy</option>
            <option>Unhealthy</option>
          </select>

          <select className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
            <option>All sources</option>
          </select>

          <select className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
            <option>All destinations</option>
          </select>

          <button className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-700 hover:bg-gray-50 flex items-center">
            Tags <ChevronDown className="w-3 h-3 ml-2" />
          </button>
        </div>

        {/* Chart */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <div className="flex justify-end mb-4">
            <div className="flex text-xs rounded-md border border-gray-200 overflow-hidden">
              <button className="px-2 py-1 bg-gray-50 text-gray-500 border-r border-gray-200">6h</button>
              <button className="px-2 py-1 bg-gray-50 text-gray-500 border-r border-gray-200">24h</button>
              <button className="px-2 py-1 bg-primary-50 text-primary-600 font-medium border-r border-gray-200">7d</button>
              <button className="px-2 py-1 bg-gray-50 text-gray-500">30d</button>
            </div>
          </div>
          <div className="h-48 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={MOCK_CHART_DATA} barSize={12}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10, fill: '#6B7280' }}
                  dy={10}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10, fill: '#6B7280' }}
                />
                <Tooltip
                  contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                  cursor={{ fill: '#F3F4F6' }}
                />
                <Bar dataKey="active" fill="#06B6D4" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="w-12 px-6 py-3 text-left text-xs font-bold text-primary-600 uppercase tracking-wider"></th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-primary-600 uppercase tracking-wider">Name</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-primary-600 uppercase tracking-wider">Source Type</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-primary-600 uppercase tracking-wider">Frequency</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-primary-600 uppercase tracking-wider">Tags</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-primary-600 uppercase tracking-wider">Last Sync</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-primary-600 uppercase tracking-wider">Enabled</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredConnections.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500 text-sm">
                      No connections found
                    </td>
                  </tr>
                ) : (
                  filteredConnections.map((connection) => (
                    <tr
                      key={connection.id}
                      onClick={() => navigate(`/datasources/${connection.id}`)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        {connection.status === "failed" || connection.lastSyncStatus === "failed" ? (
                          <XCircle className="w-5 h-5 text-red-500" />
                        ) : connection.status === "inactive" ? (
                          <Pause className="w-5 h-5 text-gray-400" />
                        ) : (
                          <CheckCircle className="w-5 h-5 text-cyan-500" />
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{connection.connectorName}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {connection.iconUrl ? (
                            <img
                              src={connection.iconUrl}
                              alt={connection.sourceType}
                              className="h-6 w-6 mr-2 rounded-sm"
                            />
                          ) : (
                            <div className="h-6 w-6 rounded-full bg-gray-100 flex items-center justify-center mr-2 text-xs font-bold text-gray-600">
                              {connection.sourceType ? connection.sourceType.charAt(0) : "?"}
                            </div>
                          )}
                          <span className="text-sm text-gray-500">{connection.sourceType || "Unknown"}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {connection.syncFrequency || "Manual"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        -
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatRelativeTime(connection.lastSyncAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={(e) => handleToggleStatus(connection, e)}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 ${connection.status === "active" ? "bg-primary-600" : "bg-gray-200"
                            }`}
                        >
                          <span
                            aria-hidden="true"
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${connection.status === "active" ? "translate-x-5" : "translate-x-0"
                              }`}
                          />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Connections;
